import { defineStore } from 'pinia'
import { subscriptionApiAxiosInstance } from '@/helpers/request'
import { isSameDay, parseISO } from 'date-fns'

const DOWNGRADE_PRODUCTS_MAP = {
	prd_nGj0qIOxEggu: 'prd_ktsg3725cCqj',
	prd_4NTYz5Tu3L4K: 'prd_zwnL1Jn4ngIJ',
	prd_sbY0NWq9GMkz: 'prd_zwnL1Jn4ngIJ',
	prd_kvFfWmOs3D4D: 'prd_Wt8CBPPqAsYR',
	prd_EYnNTjPZUV4P: 'prd_eDxdOig8hIKC',
	prd_WENKtLRaJui0: 'prd_eDxdOig8hIKC',
}

export function findDowngradeProduct(id) {
	return DOWNGRADE_PRODUCTS_MAP[normalizeProductId(id)] ?? null
}

function normalizeProductId(id) {
	return id.replace(/^(.{9}).*(.{7})$/, '$1$2')
}

function isDowngradedProduct(id) {
	return Boolean(id) && Object.values(DOWNGRADE_PRODUCTS_MAP).includes(normalizeProductId(id))
}

export const useSubscriptionStore = defineStore('subs', {
	state: () => ({
		subscription: null,
	}),

	getters: {
		hasSubscription: (state) => {
			return Boolean(state.subscription && Object.keys(state.subscription).length)
		},

		getPhases: (state) => {
			const phases = state.subscription?.phases ?? []

			return phases.reduce((map, phase) => {
				const iterationPrices = phase.iteration_prices ?? []
				const iterationPrice = iterationPrices.find(
					(price) => phase.is_active && price.iteration === state.subscription?.billing_period_number,
				)

				map[phase.phase_type] = {
					...phase,
					price: iterationPrice?.price ?? phase.price,
					discount: iterationPrice?.discount ?? null,
				}
				return map
			}, {})
		},

		getSubscription() {
			const phases = this.getPhases
			const subscription = this.subscription

			if (!this.hasSubscription) {
				return {}
			}

			return {
				...(phases.regular?.currency && { currency: phases.regular.currency }),
				...subscription,
				...phases,
			}
		},

		getCurrency() {
			return this.getSubscription?.currency?.toLowerCase?.()
		},

		getOriginalProductId() {
			return normalizeProductId(this.getSubscription?.product_id)
		},

		getActivePhase() {
			return Object.values(this.getPhases).find((phase) => phase.is_active)
		},

		checkPeriodType() {
			const activePhase = this.getActivePhase

			return {
				isCommitment: activePhase?.phase_type === 'commitment',
				isTrial: activePhase?.phase_type === 'trial',
				isRegular: activePhase?.phase_type === 'regular',
			}
		},

		checkProvider() {
			const provider = this.getSubscription?.provider

			return {
				isPalta: provider === 'palta',
				isStripe: provider === 'stripe',
				isBraintree: provider === 'braintree',
				isPrimer: provider === 'primer',
				isFlex: provider === 'flex',
			}
		},

		checkStatus() {
			return {
				isActive: Boolean(this.hasSubscription && !this.subscription?.cancelled),
				isPaused: Boolean(this.subscription?.is_paused),
				isCancelled: Boolean(this.subscription?.cancelled),
				isExpired: Boolean(this.subscription?.is_expired),
				isOverdue: Boolean(this.subscription?.is_overdue),
				isDowngraded: isDowngradedProduct(this.subscription?.product_id),
				isPendingDowngrade: isDowngradedProduct(this.subscription?.scheduled?.product_id),
			}
		},

		isCancelledViaBreakContract: (state) => {
			const isCancelled = Boolean(state.subscription?.cancelled)
			const expiredAt = state.subscription?.expired_at
			const currentPeriodEndAt = state.subscription?.current_period_end_at

			if (!isCancelled || !expiredAt || !currentPeriodEndAt) {
				return false
			}

			return isSameDay(parseISO(expiredAt), parseISO(currentPeriodEndAt))
		},

		checkBillingIntervalType() {
			return {
				isWeek: this.getSubscription?.regular?.interval_unit === 'week',
				isMonth: this.getSubscription?.regular?.interval_unit === 'month',
				isYear: this.getSubscription?.regular?.interval_unit === 'year',
				isDays: this.getSubscription?.regular?.interval_unit === 'days',
			}
		},

		checkFundingType() {
			return {
				credit: this.getSubscription?.funding_type === 'CREDIT',
			}
		},

		isTrialEqual() {
			return (duration, unit) => {
				return (
					this.getSubscription?.trial?.interval_duration === duration &&
					this.getSubscription?.trial?.interval_unit === unit
				)
			}
		},

		getIsLegacy() {
			return [this.checkProvider.isStripe, this.checkProvider.isBraintree].some(Boolean)
		},

		getIsModern() {
			return [this.checkProvider.isPalta, this.checkProvider.isPrimer, this.checkProvider.isFlex].some(Boolean)
		},

		canUpgrade() {
			return this.getIsModern && !this.checkPeriodType.isRegular
		},

		canPause() {
			return this.getIsModern && this.getSubscription.is_pause_allowed
		},

		canResume() {
			return this.getSubscription.is_paused
		},

		canPurchaseCancelOffer() {
			if (this.getSubscription.payment_method === 'pix_automatico') {
				return false
			}

			return this.getSubscription.is_cancel_offer_allowed
		},

		canDowngrade() {
			if (!this.hasSubscription || !this.checkProvider.isPrimer || this.checkStatus.isCancelled) {
				return false
			}

			const normalizedSubscriptionId = normalizeProductId(this.getSubscription.product_id)

			return DOWNGRADE_PRODUCTS_MAP[normalizedSubscriptionId] && !this.checkStatus.isPendingDowngrade
		},

		canUseFeature() {
			return (feature) => {
				switch (feature) {
					case 'upgrade':
						return this.canUpgrade
					case 'pause':
						return this.canPause
					case 'resume':
						return this.canResume
					case 'purchaseCancelOffer':
						return this.canPurchaseCancelOffer
					case 'downgrade':
						return this.canDowngrade
					default:
						return false
				}
			}
		},
	},

	actions: {
		setSubscription(subscription) {
			if (!subscription) {
				return
			}

			if (subscription.scheduled) {
				if (isDowngradedProduct(subscription?.product_id) || isDowngradedProduct(subscription?.scheduled?.product_id)) {
					subscription.cancelled = false
					subscription.cancelled_at = null
					subscription.subscription_id = subscription.scheduled.subscription_id
				} else {
					subscription.scheduled.phases[0].is_active = true
					subscription = {
						...subscription,
						...subscription.scheduled,
					}
				}
			}
			this.subscription = subscription
		},

		subscriptionMarkCanceled() {
			this.subscription.cancelled = true
		},

		async fetchSubscription({ customer_id, includeExpired = false }) {
			try {
				const params = {}

				if (includeExpired) {
					params.include_expired = true
				}

				const response = await subscriptionApiAxiosInstance.get(`/billing/v2/users/${customer_id}/subscription`, {
					timeout: 5000,
					params,
				})
				this.setSubscription(response.data)
			} catch (error) {
				this.setSubscription(null)
			}

			return this.getSubscription
		},
	},
})
